/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import { FilterDropdown } from 'components/FilterDropdown/FilterDropdown';
import { FiltersApplied } from 'components/FiltersApplied/FiltersApplied';
import { useFiltersModalContext } from 'components/FiltersModalProvider/FiltersModalProvider';
import { useFiltersContext } from 'components/FiltersProvider/FiltersProvider';
import { FilterIcon } from 'components/Icon';
import { DEFAULT_INITIAL_ADULTS, DEFAULT_INITIAL_CHILDREN } from 'components/NumberOfGuests/constants';
import { BasicButton } from 'components/buttons/BasicButton/BasicButton';
import { useAppContext } from 'contexts/appContext';
import { useMedia } from 'hooks/useMedia';
import { useCallback, useMemo } from 'react';
import { sizes } from 'styles/sizes';
import { NUMBER_OF_GUESTS_FILTER_KEY } from '../constants';
import { CruiseSearchWidgetProps, FilterDropdownConfig } from '../types';
import { getTotalSelectionForFilter } from '../utils/reducers';
import styles from './BaseCruiseSearchWidget.css';

export function BaseCruiseSearchWidget(props: Readonly<CruiseSearchWidgetProps>) {
	const { activeFilter, setActiveFilter, enabledFilters, historicalSelection, selection, appliedHistoricalSelection, filterKeyToCustomFilterConfig } =
		useFiltersContext();
	const { setShowFiltersModal, showFiltersModal, isDefaultOccupancySelected } = useFiltersModalContext();
	const { dictionary } = useAppContext();
	const isDesktop = useMedia(`(min-width: ${sizes.desktop}px)`);
	const isTablet = useMedia(`(min-width: ${sizes.tabletPortrait}px)`);
	const { actionAllFilters: allFiltersLabel, labelAllFiltersApplied } = dictionary;

	const counterLabel =
		historicalSelection.length > 0 ? `${allFiltersLabel} (${historicalSelection.length} ${labelAllFiltersApplied})` : allFiltersLabel;
	const showAllFiltersCaret = historicalSelection.length > 0 && isTablet;

	const handleFilterClick = useCallback(
		(filter: FilterDropdownConfig) => {
			setActiveFilter((current) => {
				setShowFiltersModal(filter.key !== current?.key);
				return filter.key !== activeFilter?.key ? filter : null;
			});
		},
		[activeFilter?.key, setActiveFilter, setShowFiltersModal],
	);

	const isPinned = useMemo(() => {
		if (showFiltersModal) {
			return true;
		}

		return props.isPinned;
	}, [showFiltersModal, props.isPinned]);

	const getSelectionAmount = useCallback(
		(filter: FilterDropdownConfig) => {
			if (filter.key === NUMBER_OF_GUESTS_FILTER_KEY) {
				if (selection.adults[0] !== DEFAULT_INITIAL_ADULTS.toString() || selection.children[0] !== DEFAULT_INITIAL_CHILDREN.toString())
					return selection.adults.length || selection.children.length;
			} else return getTotalSelectionForFilter(historicalSelection, filter, filterKeyToCustomFilterConfig, props.enableUniqueFilters)?.length;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[historicalSelection, props.enableUniqueFilters, isDefaultOccupancySelected, selection.adults, selection.children],
	);

	return (
		<div
			className={clsx(styles.root, { [styles.pinnedClass]: isPinned, [styles.isModalOpened]: showFiltersModal })}
			data-testid="cruise-filter-container"
			id={props.containerId}
		>
			<div className={styles.container}>
				<div className={styles.allFilters}>
					<BasicButton
						testId="show-all-filters-button"
						label={counterLabel}
						icon={FilterIcon}
						showCaret={showAllFiltersCaret}
						variant="fullColor"
						customClass={styles.custom}
						onClick={() => setShowFiltersModal(true)}
					/>
				</div>
				<div className={styles.wrapper}>
					<div className={styles.filters}>
						{enabledFilters?.map((filter) => (
							<FilterDropdown
								filterKey={filter.key}
								key={filter.label.replace(/ /g, '_').toLowerCase()}
								testId={filter.desktopTestId}
								label={dictionary[filter.dictionaryLabel]}
								isSelected={filter.key === activeFilter?.key}
								amount={getSelectionAmount(filter)}
								isFullColor={filter.isFullColor}
								showCaret={true}
								showBadge={filter.key !== NUMBER_OF_GUESTS_FILTER_KEY}
								onClick={() => handleFilterClick(filter)}
							/>
						))}
					</div>
					{isDesktop && appliedHistoricalSelection.length > 0 && <FiltersApplied />}
				</div>
			</div>
		</div>
	);
}
